import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { useSettings } from '@/context/SettingsContext';
import { 
  Package, CheckCircle, AlertTriangle, Truck, Building2,
  Camera, Upload, ArrowLeft, Save, FileText, MapPin,
  Clock, User, Calendar, Info, ImageIcon, X, Check, ChevronsUpDown,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Searchable rack picker. Replaces the old `<datalist>` which dumped
 * the entire rack catalogue without filtering — unusable for tenants
 * with hundreds of racks. Now opens a Popover with a Command box that
 * type-filters as the receiver types AND only allows picking racks
 * that already exist (no more silent ad-hoc creation — see
 * enterprise_module.py rack-existence guard for the matching backend
 * rule).
 */
const RackPicker = ({ value, onChange, racks, disabled, testId }) => {
  const [open, setOpen] = useState(false);
  const list = Array.isArray(racks) ? racks : [];
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-32 justify-between bg-wt-bg border-wt-border text-wt-text-primary text-sm h-9 px-2"
          data-testid={testId}
        >
          <span className="truncate">{value || 'Pick rack'}</span>
          <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-0 bg-wt-paper border-wt-border" align="start">
        <Command className="bg-transparent">
          <CommandInput placeholder="Search rack..." className="h-9 text-sm" />
          <CommandList className="max-h-56">
            {list.length === 0 ? (
              <CommandEmpty className="py-4 text-xs text-wt-text-muted">
                No racks for this location. Add one in Inventory → Settings → Racks.
              </CommandEmpty>
            ) : (
              <CommandEmpty className="py-2 text-xs text-wt-text-muted">No match.</CommandEmpty>
            )}
            <CommandGroup>
              {value ? (
                <CommandItem
                  value="__clear__"
                  onSelect={() => { onChange(''); setOpen(false); }}
                  className="text-xs text-wt-text-muted"
                >
                  <X className="mr-2 h-3 w-3" /> Clear (use Default)
                </CommandItem>
              ) : null}
              {list.map((r) => {
                const code = r.rack_number;
                const selected = value === code;
                return (
                  <CommandItem
                    key={r.id || code}
                    value={code}
                    onSelect={() => { onChange(code); setOpen(false); }}
                    className="text-sm"
                  >
                    <Check className={`mr-2 h-3 w-3 ${selected ? 'opacity-100' : 'opacity-0'}`} />
                    {code}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const GoodsReceiptPage = () => {
  const { poId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { formatCurrency } = useSettings();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [po, setPo] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [locations, setLocations] = useState([]);
  const [grnSubmitted, setGrnSubmitted] = useState(false);
  const [hasPendingItems, setHasPendingItems] = useState(false);
  const [grnRecords, setGrnRecords] = useState([]);
  const [totalOrdered, setTotalOrdered] = useState(0);
  const [totalReceived, setTotalReceived] = useState(0);
  
  // GRN form data - line items with received/damaged quantities
  const [lineItems, setLineItems] = useState([]);
  const [grnNotes, setGrnNotes] = useState('');
  // Racks per location — keyed by location_id so changing location on
  // a line item swaps the rack dropdown's options. Lazy-loaded the
  // first time a location is rendered. Auto-creation in the backend
  // means an empty list isn't a hard block.
  const [racksByLocation, setRacksByLocation] = useState({});
  
  // Damage images
  const [damageImages, setDamageImages] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageInputRef = useRef(null);

  // Received-product images (MANDATORY — at least one required to submit GRN).
  const [receivedImages, setReceivedImages] = useState([]);
  const [uploadingReceivedImage, setUploadingReceivedImage] = useState(false);
  const receivedImageInputRef = useRef(null);
  
  // Success dialog
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [grnResult, setGrnResult] = useState(null);

  useEffect(() => {
    if (poId) {
      loadGRNDetails();
    }
  }, [poId]);

  const loadGRNDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/enterprise/purchase-orders/${poId}/grn`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPo(response.data.po);
      setVendor(response.data.vendor);
      setLocations(response.data.locations || []);
      setGrnSubmitted(response.data.grn_submitted);
      setHasPendingItems(response.data.has_pending_items);
      setGrnRecords(response.data.grn_records || []);
      setTotalOrdered(response.data.total_ordered || 0);
      setTotalReceived(response.data.total_received || 0);
      
      // Initialize line items for GRN
      // For items with existing received_quantity, show remaining to receive
      const lines = (response.data.po?.line_items || []).map((line, index) => {
        const ordered = line.quantity || 0;
        const alreadyReceived = line.received_quantity || 0;
        const alreadyDamaged = line.damaged_quantity || 0;
        const picked = line.picked_quantity !== undefined ? line.picked_quantity : ordered;
        
        // Calculate pending (what's left to receive)
        const pending = ordered - alreadyReceived - alreadyDamaged;
        
        return {
          line_id: line.id || `legacy-line-${index}`,
          sku: line.sku,
          product_title: line.product_title,
          image_url: line.image_url,
          ordered_quantity: ordered,
          picked_quantity: picked,
          already_received: alreadyReceived,  // What was received in previous GRNs
          already_damaged: alreadyDamaged,
          pending_quantity: pending,  // What's still pending
          // For the form - new quantities to add in this GRN
          received_quantity: 0,  // Start at 0 for new GRN submission
          damaged_quantity: 0,
          location_id: line.location_id || response.data.po?.warehouse_location_id || '',
          location_name: line.location_name || response.data.po?.warehouse_location_name || '',
          // Rack inside the warehouse where these items will be placed.
          // Optional — empty / "Default" both bucket to the Default
          // rack on the rack-wise inventory aggregator. Receivers
          // typically pick from a dropdown of existing racks; backend
          // will auto-create the rack if a new code is typed.
          rack_number: line.rack_number || '',
          notes: line.notes || '',
          update_inventory: line.update_inventory !== false
        };
      });
      setLineItems(lines);
      
      // Load existing damage images
      setDamageImages(response.data.po?.grn_damage_images || []);
      // Load pending received-product images (buffer is cleared on submit,
      // so this is usually empty on a fresh session — kept so the user
      // can navigate away & back mid-upload without losing photos).
      setReceivedImages(response.data.po?.grn_received_images || []);
      setGrnNotes(response.data.po?.grn_notes || '');

      // Pre-fetch racks for every distinct location used on this PO.
      // Single round-trip per location, cached in `racksByLocation`.
      try {
        const locIds = Array.from(new Set(lines.map((l) => l.location_id).filter(Boolean)));
        const token2 = localStorage.getItem('token');
        const fetched = {};
        await Promise.all(locIds.map(async (locId) => {
          try {
            const r = await axios.get(
              `${API_BASE_URL}/enterprise/warehouse/racks?location_id=${locId}`,
              { headers: { Authorization: `Bearer ${token2}` } }
            );
            fetched[locId] = r.data?.racks || r.data || [];
          } catch {
            fetched[locId] = [];
          }
        }));
        setRacksByLocation(fetched);
      } catch (e) {
        console.error('Failed to pre-fetch racks:', e);
      }
    } catch (error) {
      toast.error('Failed to load PO details');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const updateLineItem = (index, field, value) => {
    const updated = [...lineItems];
    updated[index] = { ...updated[index], [field]: value };
    
    // If location changes, update location_name AND lazy-fetch racks
    // for the new location (the initial pre-fetch on load only covers
    // the locations the PO opened with — changing to a different
    // warehouse via the dropdown would otherwise show "No racks for
    // this location" even when racks exist there).
    if (field === 'location_id') {
      const loc = locations.find(l => l.id === value);
      updated[index].location_name = loc?.name || '';
      // Clear stale rack when location changes — the previously
      // selected rack belongs to the OLD location.
      updated[index].rack_number = '';
      if (value && racksByLocation[value] === undefined) {
        // Fire-and-forget; the picker will re-render once state lands.
        (async () => {
          try {
            const token = localStorage.getItem('token');
            const r = await axios.get(
              `${API_BASE_URL}/enterprise/warehouse/racks?location_id=${value}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            setRacksByLocation(prev => ({
              ...prev,
              [value]: r.data?.racks || r.data || [],
            }));
          } catch (e) {
            console.error('[GRN] rack fetch on location change failed:', e);
            setRacksByLocation(prev => ({ ...prev, [value]: [] }));
          }
        })();
      }
    }
    
    setLineItems(updated);
  };

  const setAllReceived = () => {
    const updated = lineItems.map(line => ({
      ...line,
      // Only set pending quantity (what's left to receive)
      received_quantity: line.pending_quantity,
      damaged_quantity: 0
    }));
    setLineItems(updated);
    toast.success('All pending items marked as received');
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    setUploadingImage(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API_BASE_URL}/enterprise/purchase-orders/${poId}/grn/damage-image`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      
      setDamageImages([...damageImages, response.data.file_url]);
      toast.success('Damage image uploaded');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImage = (index) => {
    const updated = [...damageImages];
    updated.splice(index, 1);
    setDamageImages(updated);
  };

  const handleReceivedImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    setUploadingReceivedImage(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API_BASE_URL}/enterprise/purchase-orders/${poId}/grn/received-image`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );

      setReceivedImages(prev => [...prev, response.data.file_url]);
      toast.success('Received product photo uploaded');
    } catch (error) {
      toast.error('Failed to upload photo');
    } finally {
      setUploadingReceivedImage(false);
      // Reset the input so the same file can be re-picked if needed.
      if (receivedImageInputRef.current) {
        receivedImageInputRef.current.value = '';
      }
    }
  };

  const removeReceivedImage = async (index) => {
    const fileUrl = receivedImages[index];
    // Optimistic UI update
    setReceivedImages(prev => prev.filter((_, i) => i !== index));
    // Best-effort cleanup on the server so the PO buffer stays in sync.
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_BASE_URL}/enterprise/purchase-orders/${poId}/grn/received-image`,
        {
          headers: { Authorization: `Bearer ${token}` },
          data: { file_url: fileUrl },
        }
      );
    } catch (err) {
      // Non-fatal — the next submit will ignore the stale URL from the
      // buffer since we re-send the authoritative list via `received_images`.
      console.warn('[GRN] remove image failed:', err?.response?.data || err?.message);
    }
  };

  const handleSubmitGRN = async () => {
    // Validate - MANDATORY: at least one received-product image.
    if (!receivedImages || receivedImages.length === 0) {
      toast.error('Please upload at least one photo of the received products before submitting.');
      return;
    }

    // Validate - check if any items have quantities entered
    const hasAnyReceived = lineItems?.some(l => (l?.received_quantity || 0) > 0);
    if (!hasAnyReceived) {
      toast.error('Please enter received quantities for at least one item');
      return;
    }
    
    // Validate - received + damaged cannot exceed pending/remaining quantity
    for (const line of lineItems || []) {
      const received = line?.received_quantity || 0;
      const damaged = line?.damaged_quantity || 0;
      const pending = line?.pending_quantity || 0;
      if (received + damaged > pending) {
        toast.error(`${line?.sku || 'Item'}: Received + Damaged (${received + damaged}) cannot exceed pending quantity (${pending})`);
        return;
      }
    }
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      
      const payload = {
        line_items: (lineItems || []).map(line => ({
          line_id: line?.line_id,
          sku: line?.sku,  // Include SKU for matching
          received_quantity: line?.received_quantity || 0,
          damaged_quantity: line?.damaged_quantity || 0,
          location_id: line?.location_id || '',
          location_name: line?.location_name || '',
          rack_number: line?.rack_number || null,
          notes: line?.notes || ''
        })),
        notes: grnNotes || '',
        verified_by: user?.id,
        verified_by_name: user?.full_name,
        received_images: receivedImages,
      };
      
      const response = await axios.post(
        `${API_BASE_URL}/enterprise/purchase-orders/${poId}/grn`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setGrnResult(response.data);
      setSuccessDialogOpen(true);
      setGrnSubmitted(true);
      // Reset for any subsequent "Additional GRN" submission — fresh
      // proof-of-delivery photos and rack/quantity entries are
      // required for each new GRN. The line-items table reloads via
      // the parent fetch so remaining-quantity counters are accurate.
      setReceivedImages([]);
    } catch (error) {
      console.error('GRN submission error:', error);
      // Handle Pydantic validation errors (array of objects) or string error
      let errorMessage = 'Failed to submit GRN';
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        // Pydantic validation error format
        errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      }
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Calculate totals (with null safety)
  // For display: show cumulative totals including what's already received
  const calcTotalOrdered = lineItems?.reduce((sum, l) => sum + (l?.ordered_quantity || 0), 0) || 0;
  const calcAlreadyReceived = lineItems?.reduce((sum, l) => sum + (l?.already_received || 0), 0) || 0;
  const calcAlreadyDamaged = lineItems?.reduce((sum, l) => sum + (l?.already_damaged || 0), 0) || 0;
  
  // What's being added in this GRN
  const thisGrnReceived = lineItems?.reduce((sum, l) => sum + (l?.received_quantity || 0), 0) || 0;
  const thisGrnDamaged = lineItems?.reduce((sum, l) => sum + (l?.damaged_quantity || 0), 0) || 0;
  
  // Cumulative totals for display
  const displayTotalReceived = calcAlreadyReceived + thisGrnReceived;
  const displayTotalDamaged = calcAlreadyDamaged + thisGrnDamaged;
  const displayTotalPending = calcTotalOrdered - displayTotalReceived - displayTotalDamaged;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-wt-gold"></div>
      </div>
    );
  }

  if (!po) {
    return (
      <div className="p-6 text-center">
        <AlertTriangle className="h-12 w-12 text-red-400 mx-auto mb-4" />
        <h2 className="text-xl text-wt-text-primary">Purchase Order Not Found</h2>
        <Button onClick={() => navigate('/purchase-orders')} className="mt-4">
          Back to Purchase Orders
        </Button>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="grn-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/purchase-orders')}
            className="text-wt-text-muted hover:text-wt-text-primary"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold font-playfair text-wt-text-primary flex items-center gap-3">
              <Package className="h-8 w-8 text-wt-gold" />
              Goods Receipt
            </h1>
            <p className="text-wt-text-muted mt-1">
              Verify received goods for {po.po_number}
            </p>
          </div>
        </div>
        
        {grnSubmitted && (
          <div className="flex flex-col items-end gap-2">
            <Badge className="bg-green-500/20 text-green-400 border-0 text-sm px-4 py-2">
              <CheckCircle className="h-4 w-4 mr-2" />
              GRN Submitted: {po.grn_number}
            </Badge>
            {hasPendingItems && (
              <Badge className="bg-orange-500/20 text-orange-400 border-0 text-sm px-3 py-1">
                <Clock className="h-3 w-3 mr-1" />
                {displayTotalPending} items pending
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* PO Info Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-wt-paper border border-wt-border rounded-xl p-6"
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div>
            <p className="text-sm text-wt-text-muted flex items-center gap-2">
              <FileText className="h-4 w-4" /> PO Number
            </p>
            <p className="text-wt-gold font-bold text-lg">{po.po_number}</p>
          </div>
          <div>
            <p className="text-sm text-wt-text-muted flex items-center gap-2">
              <Building2 className="h-4 w-4" /> Vendor
            </p>
            <p className="text-wt-text-primary font-medium">{po.vendor_name}</p>
            <p className="text-xs text-wt-text-muted">{vendor?.vendor_code}</p>
          </div>
          <div>
            <p className="text-sm text-wt-text-muted flex items-center gap-2">
              <Calendar className="h-4 w-4" /> Expected Delivery
            </p>
            <p className="text-wt-text-primary">{po.expected_delivery_date || 'Not set'}</p>
          </div>
          <div>
            <p className="text-sm text-wt-text-muted flex items-center gap-2">
              <Truck className="h-4 w-4" /> Fulfillment
            </p>
            <p className="text-wt-text-primary">
              {po.fulfillment_method === 'wooden_twist_pickup' ? 'Pickup' : 'Vendor Delivers'}
            </p>
          </div>
        </div>
      </motion.div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Ordered', value: calcTotalOrdered, icon: Package, color: 'text-wt-gold' },
          { label: 'Received', value: displayTotalReceived, icon: CheckCircle, color: 'text-green-400' },
          { label: 'Damaged', value: displayTotalDamaged, icon: AlertTriangle, color: 'text-red-400' },
          { label: 'Pending', value: displayTotalPending, icon: Clock, color: 'text-orange-400' }
        ].map((stat, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            className="bg-wt-paper border border-wt-border rounded-xl p-4"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-wt-text-muted text-sm">{stat.label}</p>
                <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
              </div>
              <stat.icon className={`h-8 w-8 ${stat.color} opacity-50`} />
            </div>
          </motion.div>
        ))}
      </div>

      {/* Quick Actions - show if not submitted OR if there are pending items */}
      {(!grnSubmitted || hasPendingItems) && (
        <div className="flex gap-3">
          <Button 
            onClick={setAllReceived}
            variant="outline"
            className="border-green-500 text-green-400 hover:bg-green-500/10"
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            Mark All Pending as Received
          </Button>
          {grnSubmitted && hasPendingItems && (
            <Badge className="bg-blue-500/20 text-blue-400 border-0 px-3 py-2">
              <Info className="h-4 w-4 mr-2" />
              Submit additional GRN for remaining items
            </Badge>
          )}
        </div>
      )}

      {/* Line Items Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-wt-paper border border-wt-border rounded-xl overflow-hidden"
      >
        <div className="p-4 border-b border-wt-border">
          <h2 className="text-lg font-semibold text-wt-text-primary">Line Items</h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-wt-subtle">
              <tr>
                <th className="text-left p-4 text-wt-text-muted text-sm">Product</th>
                <th className="text-center p-4 text-wt-text-muted text-sm">Ordered</th>
                <th className="text-center p-4 text-wt-text-muted text-sm">Picked</th>
                <th className="text-center p-4 text-wt-text-muted text-sm">Received</th>
                <th className="text-center p-4 text-wt-text-muted text-sm">Damaged</th>
                <th className="text-left p-4 text-wt-text-muted text-sm">Location</th>
                <th className="text-left p-4 text-wt-text-muted text-sm">Rack</th>
                <th className="text-left p-4 text-wt-text-muted text-sm">Notes</th>
              </tr>
            </thead>
            <tbody>
              {lineItems.map((line, index) => {
                const canReceiveMore = line.pending_quantity > 0;
                const maxReceive = line.pending_quantity;
                
                return (
                <tr key={line.line_id} className="border-t border-wt-border/50 hover:bg-wt-subtle/30">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      {line.image_url ? (
                        <img src={line.image_url} alt="" className="w-10 h-10 rounded object-cover" />
                      ) : (
                        <div className="w-10 h-10 bg-wt-bg rounded flex items-center justify-center">
                          <Package className="h-4 w-4 text-wt-text-muted" />
                        </div>
                      )}
                      <div>
                        <p className="text-wt-gold font-medium">{line.sku}</p>
                        <p className="text-wt-text-primary text-sm">{line.product_title}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4 text-center">
                    <span className="text-wt-text-muted">{line.ordered_quantity}</span>
                  </td>
                  <td className="p-4 text-center">
                    <span className={`font-semibold ${line.picked_quantity < line.ordered_quantity ? 'text-orange-400' : 'text-green-400'}`}>
                      {line.picked_quantity}
                    </span>
                    {line.picked_quantity < line.ordered_quantity && (
                      <p className="text-xs text-orange-400">Partial</p>
                    )}
                  </td>
                  <td className="p-4">
                    <div className="flex flex-col items-center gap-1">
                      {/* Show already received */}
                      {line.already_received > 0 && (
                        <span className="text-xs text-green-400">
                          Previously: {line.already_received}
                        </span>
                      )}
                      {/* Input for new receipt */}
                      <Input
                        type="number"
                        min="0"
                        max={maxReceive}
                        value={line.received_quantity}
                        onChange={(e) => {
                          const val = parseInt(e.target.value) || 0;
                          updateLineItem(index, 'received_quantity', Math.min(Math.max(0, val), maxReceive));
                        }}
                        className={`w-20 text-center bg-wt-bg border-wt-border text-wt-text-primary ${!canReceiveMore ? 'opacity-50' : ''}`}
                        disabled={!canReceiveMore}
                        data-testid={`received-qty-${index}`}
                      />
                      {!canReceiveMore && line.already_received > 0 && (
                        <span className="text-xs text-wt-text-muted">Complete</span>
                      )}
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex flex-col items-center gap-1">
                      {/* Show already damaged */}
                      {line.already_damaged > 0 && (
                        <span className="text-xs text-red-400">
                          Previously: {line.already_damaged}
                        </span>
                      )}
                      <Input
                        type="number"
                        min="0"
                        max={maxReceive}
                        value={line.damaged_quantity}
                        onChange={(e) => {
                          const val = parseInt(e.target.value) || 0;
                          updateLineItem(index, 'damaged_quantity', Math.min(Math.max(0, val), maxReceive));
                        }}
                        className={`w-20 text-center bg-wt-bg border-wt-border text-wt-text-primary ${!canReceiveMore ? 'opacity-50' : ''}`}
                        disabled={!canReceiveMore}
                        data-testid={`damaged-qty-${index}`}
                      />
                    </div>
                  </td>
                  <td className="p-4">
                    <Select 
                      value={line.location_id} 
                      onValueChange={(v) => updateLineItem(index, 'location_id', v)}
                      disabled={!canReceiveMore}
                    >
                      <SelectTrigger className="w-36 bg-wt-bg border-wt-border text-wt-text-primary">
                        <SelectValue placeholder="Location" />
                      </SelectTrigger>
                      <SelectContent className="bg-wt-paper border-wt-border">
                        {locations.map(loc => (
                          <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-4">
                    {/* Searchable rack picker. Opens a Popover with
                        type-filtering. Only racks that exist for the
                        selected location are listed; backend rejects
                        any unknown rack to enforce the
                        Inventory-Settings-only creation flow. Empty
                        value = "Default" rack on the rack-wise
                        aggregator. */}
                    <RackPicker
                      value={line.rack_number || ''}
                      onChange={(v) => updateLineItem(index, 'rack_number', v)}
                      racks={racksByLocation[line.location_id] || []}
                      disabled={!canReceiveMore || !line.location_id}
                      testId={`rack-input-${index}`}
                    />
                  </td>
                  <td className="p-4">
                    <Input
                      value={line.notes}
                      onChange={(e) => updateLineItem(index, 'notes', e.target.value)}
                      placeholder="Notes..."
                      className="w-32 bg-wt-bg border-wt-border text-wt-text-primary text-sm"
                      disabled={!canReceiveMore}
                    />
                  </td>
                </tr>
              )})}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Received Product Photos — MANDATORY */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25 }}
        className={`bg-wt-paper border rounded-xl p-6 ${
          receivedImages.length === 0 && !grnSubmitted
            ? 'border-red-500/40'
            : 'border-wt-border'
        }`}
        data-testid="received-images-section"
      >
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-lg font-semibold text-wt-text-primary flex items-center gap-2">
            <Camera className="h-5 w-5 text-wt-gold" />
            Received Product Photos
            <span className="text-red-400 text-sm font-normal">* Required</span>
          </h2>
          <Badge
            className={
              receivedImages.length > 0
                ? 'bg-green-500/20 text-green-400 border border-green-500/40'
                : 'bg-red-500/20 text-red-400 border border-red-500/40'
            }
          >
            {receivedImages.length} uploaded
          </Badge>
        </div>
        <p className="text-xs text-wt-text-muted mb-4">
          Upload at least one clear photo of the goods you received from the vendor.
          This is required as proof of delivery and cannot be skipped.
        </p>

        <div className="flex flex-wrap gap-4">
          {receivedImages.map((img, i) => (
            <div
              key={i}
              className="relative w-24 h-24 rounded-lg overflow-hidden bg-wt-bg"
              data-testid={`received-image-${i}`}
            >
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}${img}`}
                alt={`Received ${i + 1}`}
                className="w-full h-full object-cover"
              />
              <button
                onClick={() => removeReceivedImage(i)}
                className="absolute top-1 right-1 p-1 bg-red-500 rounded-full text-white hover:bg-red-600"
                data-testid={`remove-received-image-${i}`}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}

          {/* Upload button is ALWAYS visible — the first GRN AND every
              "Additional GRN" after it both require proof-of-delivery
              photos. After each successful submit we clear
              `receivedImages` so the next round starts fresh. */}
          <>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleReceivedImageUpload}
              ref={receivedImageInputRef}
              className="hidden"
            />
            <button
              onClick={() => receivedImageInputRef.current?.click()}
              disabled={uploadingReceivedImage}
              className={`w-24 h-24 rounded-lg border-2 border-dashed flex flex-col items-center justify-center transition-colors ${
                receivedImages.length === 0
                  ? 'border-red-500/60 hover:border-red-400 text-red-400 hover:text-red-300'
                  : 'border-wt-border hover:border-wt-gold text-wt-text-muted hover:text-wt-gold'
              }`}
              data-testid="upload-received-image-btn"
            >
              {uploadingReceivedImage ? (
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-wt-gold"></div>
              ) : (
                <>
                  <Upload className="h-6 w-6 mb-1" />
                  <span className="text-xs">Add Photo</span>
                </>
              )}
            </button>
          </>
        </div>
      </motion.div>

      {/* Damage Images */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-wt-paper border border-wt-border rounded-xl p-6"
      >
        <h2 className="text-lg font-semibold text-wt-text-primary mb-4 flex items-center gap-2">
          <ImageIcon className="h-5 w-5 text-wt-gold" />
          Damage Images (Optional)
        </h2>
        
        <div className="flex flex-wrap gap-4">
          {damageImages.map((img, i) => (
            <div key={i} className="relative w-24 h-24 rounded-lg overflow-hidden bg-wt-bg">
              <img 
                src={`${process.env.REACT_APP_BACKEND_URL}${img}`} 
                alt={`Damage ${i + 1}`}
                className="w-full h-full object-cover"
              />
              {!grnSubmitted && (
                <button
                  onClick={() => removeImage(i)}
                  className="absolute top-1 right-1 p-1 bg-red-500 rounded-full text-white hover:bg-red-600"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          ))}
          
          {!grnSubmitted && (
            <>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                ref={imageInputRef}
                className="hidden"
              />
              <button
                onClick={() => imageInputRef.current?.click()}
                disabled={uploadingImage}
                className="w-24 h-24 rounded-lg border-2 border-dashed border-wt-border hover:border-wt-gold flex flex-col items-center justify-center text-wt-text-muted hover:text-wt-gold transition-colors"
              >
                {uploadingImage ? (
                  <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-wt-gold"></div>
                ) : (
                  <>
                    <Camera className="h-6 w-6 mb-1" />
                    <span className="text-xs">Add Photo</span>
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </motion.div>

      {/* Notes */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-wt-paper border border-wt-border rounded-xl p-6"
      >
        <Label className="text-wt-text-primary text-lg font-semibold mb-4 block">
          GRN Notes
        </Label>
        <Textarea
          value={grnNotes}
          onChange={(e) => setGrnNotes(e.target.value)}
          placeholder="Any notes about the goods receipt..."
          className="bg-wt-bg border-wt-border text-wt-text-primary"
          rows={3}
          disabled={grnSubmitted}
          data-testid="grn-notes"
        />
      </motion.div>

      {/* Submit Button - Show if not submitted OR if there are pending items */}
      {(!grnSubmitted || hasPendingItems) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="flex justify-end gap-4"
        >
          <Button 
            variant="outline" 
            onClick={() => navigate('/purchase-orders')}
            className="border-wt-border text-wt-text-muted"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmitGRN}
            disabled={submitting || thisGrnReceived === 0 || receivedImages.length === 0}
            className="bg-wt-gold hover:bg-wt-gold-hover text-black px-8"
            data-testid="submit-grn-btn"
            title={receivedImages.length === 0 ? 'Upload at least one received-product photo first' : undefined}
          >
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-black mr-2"></div>
                Submitting...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                {grnSubmitted ? 'Submit Additional GRN' : 'Submit GRN'}
              </>
            )}
          </Button>
        </motion.div>
      )}

      {/* Success Dialog */}
      <Dialog open={successDialogOpen} onOpenChange={setSuccessDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary flex items-center gap-2">
              <CheckCircle className="h-6 w-6 text-green-400" />
              GRN Submitted Successfully
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
              <p className="text-green-400 font-semibold text-lg">{grnResult?.grn_number || 'GRN Created'}</p>
              <p className="text-wt-text-muted text-sm mt-1">
                PO Status: <span className="text-wt-text-primary capitalize">{(grnResult?.po_status || 'updated').replace(/_/g, ' ')}</span>
              </p>
            </div>
            
            {/* Inventory Updates */}
            {Array.isArray(grnResult?.inventory_updates) && grnResult.inventory_updates.length > 0 && (
              <div>
                <p className="text-wt-text-primary font-medium mb-2">Inventory Updated:</p>
                <div className="space-y-2">
                  {grnResult.inventory_updates.map((update, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-wt-subtle rounded">
                      <span className="text-wt-gold">{update?.sku || 'Item'}</span>
                      <span className="text-green-400">+{update?.quantity_added || 0} units</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const src = Array.isArray(grnResult?.inventory_updates) && grnResult.inventory_updates.length > 0
                      ? grnResult.inventory_updates.map(u => ({ sku: u.sku, qty: u.quantity_added || 1 }))
                      : lineItems.filter(li => (parseInt(li.received_quantity) || 0) > 0).map(li => ({ sku: li.sku, qty: parseInt(li.received_quantity) }));
                    if (!src.length) { toast.error('No received items to label'); return; }
                    const res = await axios.post(`${API_BASE_URL}/labels/generate`, { items: src },
                      { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }, responseType: 'blob' });
                    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                    window.open(url, '_blank');
                    setTimeout(() => window.URL.revokeObjectURL(url), 60000);
                  } catch (e) {
                    toast.error('Failed to generate labels');
                  }
                }}
                className="flex-1 border-wt-gold/50 text-wt-gold hover:bg-wt-gold/10"
                data-testid="grn-print-labels-btn"
              >
                Print Labels
              </Button>
              <Button 
                onClick={() => navigate('/purchase-orders')}
                className="flex-1 bg-wt-gold hover:bg-wt-gold-hover text-black"
              >
                Back to POs
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GoodsReceiptPage;
